<template>
  <div class="themed-account-management">
    <h1 class="themed-account-management__title">My Account</h1>
    
    <div class="themed-account-management__container">
      <!-- Sidebar Navigation -->
      <nav class="themed-account-management__nav">
        <ul class="themed-account-management__nav-list" role="list">
          <li>
            <button
              @click="currentSection = 'general'"
              class="themed-account-management__nav-button"
              :class="{ 'themed-account-management__nav-button--active': currentSection === 'general' }"
              type="button"
            >
              <theme-icon icon="user" type="icons" classes="w-5 h-5" />
              General
            </button>
          </li>
          <li>
            <button
              @click="currentSection = 'notifications'"
              class="themed-account-management__nav-button"
              :class="{ 'themed-account-management__nav-button--active': currentSection === 'notifications' }"
              type="button"
            >
              <theme-icon icon="bell" type="icons" classes="w-5 h-5" />
              Notifications
            </button>
          </li>
          <li v-if="hasProjectsOrSitesAccess && calendarAdminConfig.general && calendarAdminConfig.general.show_tab !== false">
            <button
              @click="currentSection = 'calendar-sync'"
              class="themed-account-management__nav-button"
              :class="{ 'themed-account-management__nav-button--active': currentSection === 'calendar-sync' }"
              type="button"
            >
              <theme-icon icon="calendar" type="icons" classes="w-5 h-5" />
              Calendar Sync
            </button>
          </li>
        </ul>
      </nav>

      <!-- Content Area -->
      <div class="themed-account-management__content">
        <div class="themed-account-management__content-inner">
          <!-- General Section -->
          <div v-if="currentSection === 'general'" class="themed-account-management__section">
            <h2 class="themed-account-management__section-title">General</h2>

            <div class="themed-account-management__form-sections">
              <!-- Personal Information -->
              <div class="themed-account-management__form-section">
                <div class="themed-account-management__field-list">
                  <!-- Full Name -->
                  <themed-form-field
                    label="Full name"
                    layout="horizontal"
                    :error="errors.name"
                    field-name="name"
                  >
                    <template #default="{ fieldId, errorId, hasError }">
                      <themed-input
                        :id="fieldId"
                        v-model="user.name"
                        type="text"
                        placeholder="Your Name"
                        :error="hasError"
                        :aria-describedby="errorId"
                      />
                    </template>
                  </themed-form-field>

                  <!-- Email Address -->
                  <themed-form-field
                    label="Email address"
                    layout="horizontal"
                    :error="errors.email"
                    field-name="email"
                    :required="true"
                  >
                    <template #default="{ fieldId, errorId, hasError, required }">
                      <themed-input
                        :id="fieldId"
                        v-model="user.email"
                        type="email"
                        placeholder="Your Email"
                        :error="hasError"
                        :aria-describedby="errorId"
                        :aria-required="required ? 'true' : null"
                      />
                    </template>
                  </themed-form-field>

                  <!-- Department -->
                  <themed-form-field
                    label="Department"
                    layout="horizontal"
                    field-name="department"
                  >
                    <template #default="{ fieldId }">
                      <themed-input
                        :id="fieldId"
                        v-model="user.department"
                        type="text"
                        placeholder="e.g. Management"
                      />
                    </template>
                  </themed-form-field>

                  <!-- Title -->
                  <themed-form-field
                    label="Title"
                    layout="horizontal"
                    field-name="title"
                  >
                    <template #default="{ fieldId }">
                      <themed-input
                        :id="fieldId"
                        v-model="user.title"
                        type="text"
                        placeholder="e.g. CFO"
                      />
                    </template>
                  </themed-form-field>

                  <!-- Cell # -->
                  <themed-form-field
                    label="Cell #"
                    layout="horizontal"
                    :error="errors.cell || errors.cell_carrier_id"
                    field-name="cell"
                  >
                    <template #default="{ fieldId, errorId, hasError }">
                      <div class="themed-account-management__cell-group">
                        <themed-input
                          :id="fieldId"
                          v-model="user.cell"
                          type="text"
                          placeholder="Your cellphone #"
                          :error="hasError"
                          :aria-describedby="errorId"
                        />
                        <themed-select
                          v-model="user.cell_carrier_id"
                          :options="cellCarrierOptions"
                          placeholder="Select carrier"
                          :error="hasError"
                        />
                      </div>
                    </template>
                  </themed-form-field>
                </div>
              </div>

              <!-- Locale Section -->
              <div class="themed-account-management__form-section">
                <h3 class="themed-account-management__subsection-title">Locale</h3>
                
                <div class="themed-account-management__field-list">
                  <themed-form-field
                    label="Country"
                    layout="horizontal"
                    field-name="country"
                  >
                    <template #default="{ fieldId }">
                      <themed-select
                        :id="fieldId"
                        v-model="user.country_id"
                        :options="countryOptions"
                      />
                    </template>
                  </themed-form-field>
                </div>
              </div>

              <!-- Reset Password Section -->
              <div class="themed-account-management__form-section">
                <h3 class="themed-account-management__subsection-title">Reset Password</h3>
                
                <div class="themed-account-management__field-list">
                  <!-- Password -->
                  <themed-form-field
                    label="Password"
                    layout="horizontal"
                    :error="errors.password"
                    field-name="password"
                  >
                    <template #default="{ fieldId, errorId, hasError }">
                      <themed-input
                        :id="fieldId"
                        v-model="password"
                        type="password"
                        :error="hasError"
                        :aria-describedby="errorId"
                      />
                    </template>
                  </themed-form-field>

                  <!-- Confirm Password -->
                  <themed-form-field
                    label="Confirm New Password"
                    layout="horizontal"
                    field-name="password_confirmation"
                  >
                    <template #default="{ fieldId }">
                      <themed-input
                        :id="fieldId"
                        v-model="password_confirmation"
                        type="password"
                      />
                    </template>
                  </themed-form-field>
                </div>
              </div>
            </div>
          </div>

          <!-- Notifications Section -->
          <div v-if="currentSection === 'notifications'" class="themed-account-management__section">
            <h2 class="themed-account-management__section-title">Notifications</h2>

            <div class="themed-account-management__notifications">
              <h3 class="themed-account-management__notifications-title">Key Date Notifications</h3>
              
              <div class="themed-account-management__info-alert">
                Notifications are sent out 1 year, 6 months and 1 month before the Key Date is due
              </div>

              <div class="themed-account-management__notification-options">
                <!-- Email Notifications -->
                <div class="themed-account-management__notification-option">
                  <themed-toggle
                    v-model="user.wants_keydate_emails"
                    label="Email me about upcoming Key Dates"
                  />
                </div>

                <!-- Task Notifications -->
                <div class="themed-account-management__notification-option">
                  <themed-toggle
                    v-model="user.wants_task_notifications"
                    label="Email me about task updates"
                  />
                </div>

                <!-- Text Notifications -->
                <div class="themed-account-management__notification-option">
                  <themed-toggle
                    v-model="user.wants_keydate_texts"
                    label="Text me about upcoming Key Dates"
                  />
                  
                  <div v-if="user.wants_keydate_texts" class="themed-account-management__text-config">
                    <themed-input
                      v-model="user.cell"
                      type="text"
                      placeholder="Your cellphone #"
                      :error="Boolean(errors.cell)"
                    />
                    <themed-select
                      v-model="user.cell_carrier_id"
                      :options="cellCarrierOptions"
                      placeholder="Select carrier"
                      :error="Boolean(errors.cell_carrier_id)"
                    />
                    <p v-if="errors.cell" class="themed-account-management__error-text">{{ errors.cell[0] }}</p>
                    <p v-if="errors.cell_carrier_id" class="themed-account-management__error-text">{{ errors.cell_carrier_id[0] }}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Calendar Sync Section -->
          <div v-if="currentSection === 'calendar-sync'" class="themed-account-management__section">
            <h2 class="themed-account-management__section-title">Calendar Sync</h2>

            <div class="themed-account-management__calendar-sync">
              <div class="themed-account-management__info-alert">
                Sync your CRESiteIQ key dates and events to your preferred calendar application.
                Events will be automatically updated when changes are made.
              </div>

              <div v-if="hasAnyProviderEnabled">
              <div class="themed-account-management__calendar-toggle">
                <themed-toggle
                  v-model="calendarSyncEnabled"
                  label="Enable Calendar Sync"
                />
              </div>

              <div v-if="calendarSyncEnabled" class="themed-account-management__calendar-providers">
                <h3 class="themed-account-management__calendar-providers-title">
                  Select Calendar Provider
                </h3>
                
                <div class="themed-account-management__provider-options">
                  <div
                    v-for="provider in calendarProviders"
                    :key="provider.value"
                    class="themed-account-management__provider-card"
                    :class="{ 'themed-account-management__provider-card--selected': selectedCalendarProvider === provider.value }"
                    @click="selectProvider(provider.value)"
                    role="radio"
                    :aria-checked="selectedCalendarProvider === provider.value ? 'true' : 'false'"
                    tabindex="0"
                    @keydown.enter="selectProvider(provider.value)"
                    @keydown.space.prevent="selectProvider(provider.value)"
                  >
                    <div class="themed-account-management__provider-icon">
                      <theme-icon :icon="provider.icon" type="icons" classes="w-8 h-8" />
                    </div>
                    <div class="themed-account-management__provider-name">
                      {{ provider.label }}
                    </div>
                    <div class="themed-account-management__provider-check">
                      <template v-if="selectedCalendarProvider === provider.value">
                        <theme-icon 
                          icon="check-circle" 
                          type="icons" 
                          classes="w-5 h-5" 
                        />
                        <span class="themed-account-management__provider-selected-label">Selected</span>
                      </template>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Event Type Selection (shown when Google Calendar or Outlook is selected) -->
              <div v-if="calendarSyncEnabled && (selectedCalendarProvider === 'google' || selectedCalendarProvider === 'outlook')" class="themed-account-management__event-types">
                <h3 class="themed-account-management__event-types-title">
                  Select Events to Sync
                </h3>
                <p class="themed-account-management__event-types-description">
                  Choose which types of events you want to sync to your {{ selectedCalendarProvider === 'outlook' ? 'Outlook Calendar' : 'Google Calendar' }}
                </p>
                
                <div class="themed-account-management__event-type-options">
                  <div v-if="calendarAdminConfig.event_types.key_dates" class="themed-account-management__event-type-item">
                    <div class="flex items-center justify-between">
                      <themed-toggle
                        v-model="syncEventTypes.keyDates"
                        label="Key Dates (Sites)"
                      />
                      <span v-if="syncedTypes['key_dates']" class="inline-flex items-center gap-1 text-xs font-medium" style="color: var(--success, #22c55e);">
                        <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/></svg>
                        Synced
                      </span>
                    </div>
                    <p class="themed-account-management__event-type-help">
                      Sync important dates from your sites and projects
                    </p>
                  </div>

                  <div v-if="calendarAdminConfig.event_types.tours" class="themed-account-management__event-type-item">
                    <div class="flex items-center justify-between">
                      <themed-toggle
                        v-model="syncEventTypes.tours"
                        label="Tours"
                      />
                      <span v-if="syncedTypes['tours']" class="inline-flex items-center gap-1 text-xs font-medium" style="color: var(--success, #22c55e);">
                        <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/></svg>
                        Synced
                      </span>
                    </div>
                    <p class="themed-account-management__event-type-help">
                      Sync scheduled property tours and site visits
                    </p>
                  </div>

                  <div v-if="calendarAdminConfig.event_types.tasks" class="themed-account-management__event-type-item">
                    <div class="flex items-center justify-between">
                      <themed-toggle
                        v-model="syncEventTypes.tasks"
                        label="Tasks"
                      />
                      <span v-if="syncedTypes['tasks']" class="inline-flex items-center gap-1 text-xs font-medium" style="color: var(--success, #22c55e);">
                        <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/></svg>
                        Synced
                      </span>
                    </div>
                    <p class="themed-account-management__event-type-help">
                      Sync your tasks and to-do items with due dates
                    </p>
                  </div>
                </div>
              </div>

              <!-- iCal Export Section (shown when iCal is selected) -->
              <div v-if="calendarSyncEnabled && selectedCalendarProvider === 'ical'" class="themed-account-management__ical-export">
                <h3 class="themed-account-management__ical-export-title">
                  Export to iCal
                </h3>
                <p class="themed-account-management__ical-export-description">
                  Download your CRESiteIQ data as an iCalendar (.ics) file that can be imported into any calendar application
                </p>
                
                <div class="themed-account-management__ical-export-options">
                  <div class="themed-account-management__ical-export-item">
                    <themed-toggle
                      v-model="icalExportOptions.tours"
                      label="Tours"
                    />
                    <p class="themed-account-management__ical-export-help">
                      Include scheduled property tours and site visits
                    </p>
                  </div>

                  <div class="themed-account-management__ical-export-item">
                    <themed-toggle
                      v-model="icalExportOptions.tasks"
                      label="Tasks"
                    />
                    <p class="themed-account-management__ical-export-help">
                      Include your tasks and to-do items with due dates
                    </p>
                  </div>

                  <div class="themed-account-management__ical-export-item">
                    <themed-toggle
                      v-model="icalExportOptions.keyDates"
                      label="Key Dates"
                    />
                    <p class="themed-account-management__ical-export-help">
                      Include important dates from your sites and projects
                    </p>
                  </div>
                </div>

                <themed-button
                  @click="downloadIcal"
                  variant="default"
                  :disabled="!canExportIcal || isExportingIcal"
                  class="themed-account-management__ical-download-button"
                >
                  {{ isExportingIcal ? 'Downloading...' : 'Download iCal File' }}
                </themed-button>
              </div>
              </div>
              <div v-else class="themed-account-management__info-alert themed-account-management__calendar-unavailable">
                <p>Calendar sync is not available</p>
              </div>
            </div>
          </div>

          <!-- Save Button -->
          <div 
            v-if="hasUnsavedChanges" 
            class="themed-account-management__unsaved-banner"
            role="status"
            aria-live="polite"
          >
            You have unsaved changes
          </div>

          <themed-button
            @click="saveUser"
            variant="default"
            :disabled="isSaving"
            class="themed-account-management__save-button"
          >
            <svg v-if="isSaving" class="animate-spin -ml-1 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
            </svg>
            {{ isSaving ? 'Saving...' : (hasUnsavedChanges ? 'Save Changes' : 'Save') }}
          </themed-button>
        </div>
      </div>
    </div>

    <div aria-live="polite" aria-atomic="true">
      <notifications classes="notification" position="bottom right"></notifications>
    </div>
    
    <div class="sr-only" role="status" aria-live="assertive" aria-atomic="true">
      {{ screenReaderAnnouncement }}
    </div>
  </div>
</template>

<script>
export default {
  name: 'ThemedAccountManagement',
  
  props: {
    countryData: {
      type: [Array, Object],
      required: true
    },
    cellCarrierData: {
      type: [Array, Object],
      required: true
    },
    userData: {
      type: [Array, Object],
      required: true
    },
    calendarAdminConfig: {
      type: Object,
      default: () => ({
        general: { show_tab: true },
        providers: { google: true, outlook: true, ical: true },
        event_types: { tasks: true, tours: true, key_dates: true }
      })
    }
  },

  data() {
    return {
      currentSection: 'general',
      user: this.userData,
      countries: this.countryData,
      password: null,
      password_confirmation: null,
      errors: {},
      
      // Calendar sync data
      calendarSyncEnabled: false,
      selectedCalendarProvider: null,
      syncEventTypes: {
        keyDates: false,
        tours: false,
        tasks: false
      },
      
      // iCal export data
      icalExportOptions: {
        tours: false,
        tasks: false,
        keyDates: false
      },
      isExportingIcal: false,
      
      // Unsaved changes tracking
      originalFormState: null,
      isSaving: false,
      screenReaderAnnouncement: ''
    };
  },

  computed: {
    countryOptions() {
      return this.countries.map(country => ({
        label: country.name,
        value: country.id
      }));
    },

    calendarProviders() {
      const all = [
        { value: 'outlook', label: 'Outlook', icon: 'outlook' },
        { value: 'google', label: 'Google Calendar', icon: 'google-calendar' },
        { value: 'ical', label: 'iCal', icon: 'ical' }
      ];
      return all.filter(p => {
        if (p.value === 'google') return this.calendarAdminConfig.providers.google;
        if (p.value === 'outlook') return this.calendarAdminConfig.providers.outlook;
        if (p.value === 'ical') return this.calendarAdminConfig.providers.ical !== false;
        return true;
      });
    },

    hasAnyProviderEnabled() {
      return this.calendarProviders.length > 0;
    },
    
    cellCarrierOptions() {      return [
        { label: '', value: '' },
        ...this.cellCarrierData.map(carrier => ({
          label: carrier.carrier,
          value: carrier.id
        }))
      ];
    },
    
    hasProjectsOrSitesAccess() {
      // Admins always have access to everything
      if (this.user.role && (this.user.role.name === 'admin' || this.user.role.name === 'Admin')) {
        return true;
      }
      
      // Check if user has module permissions for projects or sites
      if (!this.user.module_permissions) {
        return false;
      }
      
      // Check if any module with slug 'projects' or 'sites' is enabled
      const modules = Object.values(this.user.module_permissions);
      return modules.some(module => 
        (module.slug === 'projects' || module.slug === 'sites') && module.enabled
      );
    },
    
    canExportIcal() {
      return this.icalExportOptions.tours || 
             this.icalExportOptions.tasks || 
             this.icalExportOptions.keyDates;
    },

    syncedTypes() {
      if (!this.user.calendar_syncs) return {};
      const synced = {};
      this.user.calendar_syncs.forEach(sync => {
        if (sync.is_enabled && sync.calendar_provider === this.selectedCalendarProvider) {
          synced[sync.sync_type] = true;
        }
      });
      return synced;
    },
    
    currentFormState() {
      return {
        name: this.user.name,
        email: this.user.email,
        department: this.user.department,
        title: this.user.title,
        cell: this.user.cell,
        cell_carrier_id: this.user.cell_carrier_id,
        country_id: this.user.country_id,
        wants_keydate_emails: this.user.wants_keydate_emails,
        wants_keydate_texts: this.user.wants_keydate_texts,
        wants_task_notifications: this.user.wants_task_notifications,
        calendarSyncEnabled: this.calendarSyncEnabled,
        selectedCalendarProvider: this.selectedCalendarProvider,
        syncEventTypes: { ...this.syncEventTypes }
      };
    },
    
    hasUnsavedChanges() {
      if (!this.originalFormState) return false;
      return JSON.stringify(this.currentFormState) !== JSON.stringify(this.originalFormState);
    }
  },

  created() {
    window.addEventListener('beforeunload', this.handleBeforeUnload);
  },

  beforeDestroy() {
    window.removeEventListener('beforeunload', this.handleBeforeUnload);
  },

  methods: {
    announceToScreenReader(message) {
      this.screenReaderAnnouncement = '';
      this.$nextTick(() => {
        this.screenReaderAnnouncement = message;
      });
    },

    handleBeforeUnload(e) {
      if (this.hasUnsavedChanges && !this.isSaving) {
        e.preventDefault();
        e.returnValue = '';
      }
    },

    selectProvider(providerValue) {
      if (this.calendarSyncEnabled) {
        this.selectedCalendarProvider = providerValue;

        // Reset toggles and reload state for the newly selected provider
        this.syncEventTypes.keyDates = false;
        this.syncEventTypes.tours = false;
        this.syncEventTypes.tasks = false;

        if (this.user.calendar_syncs) {
          this.user.calendar_syncs.forEach(sync => {
            if (sync.is_enabled && sync.calendar_provider === providerValue) {
              if (sync.sync_type === 'key_dates') {
                this.syncEventTypes.keyDates = true;
              } else if (sync.sync_type === 'tours') {
                this.syncEventTypes.tours = true;
              } else if (sync.sync_type === 'tasks') {
                this.syncEventTypes.tasks = true;
              }
            }
          });
        }
      }
    },

    async downloadIcal() {
      if (!this.canExportIcal) {
        this.$notify({
          type: 'warning',
          title: 'No Selection',
          text: 'Please select at least one data type to export'
        });
        this.announceToScreenReader('Please select at least one data type to export');
        return;
      }

      this.isExportingIcal = true;

      try {
        const response = await axios.post('/ical/export', {
          tours: this.icalExportOptions.tours,
          tasks: this.icalExportOptions.tasks,
          key_dates: this.icalExportOptions.keyDates
        }, {
          responseType: 'blob'
        });

        // Create blob from response
        const blob = new Blob([response.data], { type: 'text/calendar' });
        
        // Create download link
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'cresiteiq-export.ics';
        
        // Trigger download
        document.body.appendChild(link);
        link.click();
        
        // Cleanup
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        this.$notify({
          type: 'success',
          title: 'Success',
          text: 'iCal file downloaded successfully'
        });
        this.announceToScreenReader('iCal file downloaded successfully');
      } catch (error) {
        const errorMessage = error.response?.data?.error || 'Failed to export iCal file. Please try again.';
        
        this.$notify({
          type: 'error',
          title: 'Export Failed',
          text: errorMessage
        });
        this.announceToScreenReader('iCal export failed. ' + errorMessage);
      } finally {
        this.isExportingIcal = false;
      }
    },

    saveUser() {
      this.errors = {};
      this.isSaving = true;

      delete this.user.password;
      delete this.user.password_confirmation;

      if (![null, ''].includes(this.password) && ![null, ''].includes(this.password_confirmation)) {
        this.user.password = this.password;
        this.user.password_confirmation = this.password_confirmation;
      }

      // Build user data object
      const userData = { ...this.user };

      // Always include calendar sync preferences in save payload
      userData.calendar_sync_enabled = this.calendarSyncEnabled;
      userData.calendar_provider = this.selectedCalendarProvider;
      userData.sync_key_dates = this.syncEventTypes.keyDates;
      userData.sync_tours = this.syncEventTypes.tours;
      userData.sync_tasks = this.syncEventTypes.tasks;

      axios.patch('/my-account/' + this.user.id, userData).then((response) => {
        localStorage.setItem(`reopt_baseCurrency`, response.data.user.country.currency.code);
        localStorage.setItem(`reopt_locale`, response.data.user.country.locale);

        // Update user data with fresh response so syncedTypes recalculates immediately
        this.user = response.data.user;

        this.password = null;
        this.password_confirmation = null;

        // Reset the original form state after successful save
        this.$nextTick(() => {
          this.originalFormState = JSON.parse(JSON.stringify(this.currentFormState));
        });

        // Check if OAuth redirect is needed (only happens when saving calendar sync)
        if (response.data.redirect_to_oauth && response.data.oauth_url) {
          // Open OAuth in a centered popup window
          const width = 600;
          const height = 700;
          const left = (screen.width - width) / 2;
          const top = (screen.height - height) / 2;
          const authWindow = window.open(
            response.data.oauth_url,
            'calendar_auth',
            `width=${width},height=${height},left=${left},top=${top},scrollbars=yes,resizable=yes`
          );

          // Poll for the popup closing, then refresh the page to calendar-sync tab
          const pollTimer = setInterval(() => {
            if (authWindow && authWindow.closed) {
              clearInterval(pollTimer);
              window.location.href = '/my-account#calendar-sync';
              window.location.reload();
            }
          }, 500);

          this.isSaving = false;
        } else {
          this.$notify({
            type: 'success',
            title: 'Success',
            text: 'Account Updated'
          });
          this.announceToScreenReader('Account settings saved successfully');
          this.isSaving = false;
        }
      }).catch(error => {
        this.isSaving = false;
        this.errors = error.response.data.errors;
        
        this.$notify({
          type: 'error',
          title: 'Error',
          text: 'Failed to update account settings'
        });
        this.announceToScreenReader('Failed to update account settings');
      });
    }
  },

  mounted() {
    // Load calendar sync preferences from user data
    if (this.user.calendar_syncs && this.user.calendar_syncs.length > 0) {
      // Check if any sync is enabled
      const hasEnabledSync = this.user.calendar_syncs.some(sync => sync.is_enabled);
      this.calendarSyncEnabled = hasEnabledSync;
      
      if (hasEnabledSync) {
        // Get the provider from the first enabled sync
        const enabledSync = this.user.calendar_syncs.find(sync => sync.is_enabled);
        this.selectedCalendarProvider = enabledSync.calendar_provider;
        
        // Load sync types only for the selected provider
        this.user.calendar_syncs.forEach(sync => {
          if (sync.is_enabled && sync.calendar_provider === this.selectedCalendarProvider) {
            if (sync.sync_type === 'key_dates') {
              this.syncEventTypes.keyDates = true;
            } else if (sync.sync_type === 'tours') {
              this.syncEventTypes.tours = true;
            } else if (sync.sync_type === 'tasks') {
              this.syncEventTypes.tasks = true;
            }
          }
        });
      }
    }

    // Snapshot the original form state after all data is loaded
    this.$nextTick(() => {
      this.originalFormState = JSON.parse(JSON.stringify(this.currentFormState));
    });

    // Check URL hash to activate a specific section (e.g. after OAuth redirect)
    if (window.location.hash === '#calendar-sync' && this.hasProjectsOrSitesAccess) {
      this.currentSection = 'calendar-sync';
      // Show success toast after OAuth completion
      const providerName = this.selectedCalendarProvider === 'outlook' ? 'Outlook' : 'Google Calendar';
      this.$nextTick(() => {
        this.$notify({
          type: 'success',
          title: 'Connected',
          text: providerName + ' connected successfully. Your events will now sync automatically.'
        });
      });
      // Clean up the hash so it doesn't persist on manual refresh
      history.replaceState(null, '', '/my-account');
    }
  }
};
</script>
